import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { services } from "@/lib/data/services";
import useSEO from "@/hooks/useSEO";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";

export default function Services() {
  useSEO({
    title: "Security Services in Manitoba | Guards, Patrols & Mining",
    description:
      "Compare guard, mobile patrol, mining, event and construction security across Manitoba. Request a plan matched to your site, hours and risks.",
    keywords: withCanadaKeywords(services.flatMap((s) => s.keywords)),
  });

  return (
    <div className="bg-[#050505] pt-32 md:pt-40">
      {/* Header */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            Index — 02
          </p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1
            data-testid="services-title"
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
          >
            Security services{" "}
            <span className="font-editorial italic font-normal text-[#a0a0a0]">
              across Manitoba
            </span>
          </h1>
          <p className="mt-8 font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl leading-relaxed">
            Fifteen disciplines. One operating standard. Every capability
            listed below is staffed by officers trained specifically for the
            environment they serve — not by generalists rotated in.
          </p>
        </div>
      </section>

      {/* Service list */}
      <section className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          {services.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/services/${s.slug}`}
                data-testid={`services-list-${s.slug}`}
                className="group grid grid-cols-12 gap-4 items-baseline border-b border-[#1e1e1e] py-8 md:py-10 hover:border-[#FF3B30] transition-colors duration-500"
              >
                <span className="col-span-2 md:col-span-1 font-editorial italic text-[#6a6a6a] group-hover:text-[#FF3B30] transition-colors text-xl md:text-2xl">
                  {s.number}
                </span>
                <div className="col-span-8 md:col-span-9">
                  <h2 className="font-display font-black uppercase tracking-tight text-white text-2xl md:text-4xl lg:text-5xl leading-none group-hover:translate-x-2 transition-transform duration-500">
                    {s.name}
                  </h2>
                  <p className="mt-3 font-editorial italic text-[#a0a0a0] text-base md:text-lg max-w-2xl">
                    {s.tag}
                  </p>
                </div>
                <span className="col-span-2 flex justify-end">
                  <ArrowUpRight
                    className="text-white/60 group-hover:text-[#FF3B30] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    size={26}
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
